import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import {
  getDataProperties,
  getColorRange,
  exportChartHandler,
  getChartInitialState,
} from "./helpers";
import getStyled from "../../../hooks/getStyled";
import { SFLoadingIcon } from "../../icons";
import { SFStack } from "../../grid";
import SFTypography from "../../typography";
import SFSkeleton from "../../skeleton";
import CustomLegend from "./customLegend";

const { styled } = getStyled();

const Wrappper = styled("div", {
  shouldForwardProp: (prop) => prop !== "loading" && prop !== "autoHeight",
})(({ theme, loading = false, autoHeight = false }) => ({
  position: "relative",
  ...(!autoHeight && { width: 0 }),
  flex: "1 1 auto",
  //this will add an opacity to the chart when loading
  //it's used to make the chart less visible when loading
  //and highlight the loading icon
  ...(loading && {
    "& .apexcharts-canvas": {
      opacity: 0.2,
      pointerEvents: "none",
    },
  }),
}));

const SFStackStyled = styled(SFStack)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  right: "50%",
  zIndex: 100,
  ".MuiSvgIcon-root": {
    animation: "spin 3s linear infinite",
  },
}));

const SFHeatmapChart = forwardRef(
  (
    {
      id = "heatmap",
      className,
      data = [],
      colors = [],
      loading = false,
      cellWidth = 50,
      cellHeight = 50,
      autoHeight = false,
      exportOptions = {
        fileName: "heatmap",
      },
      tooltipFormatter,
      legendProps = {
        legendFormatter: null,
        container: null,
      },
    },
    ref
  ) => {
    const [state, setState] = useState(
      getChartInitialState({
        id,
        data,
        colors,
        exportOptions,
        tooltipFormatter,
      })
    );

    useImperativeHandle(ref, () => ({
      export: (downloadType) => exportChartHandler(downloadType, id),
    }));

    const { t } = useTranslation();

    useEffect(() => {
      //every time data changes we need to update the chart
      //best way to indicate new data is to pass a loading prop
      //in this way we'll know when the data is changing
      //we'll update the chart data, colors and legend
      if (!loading) {
        let {
          series,
          min,
          max,
          horizontalLabelsMaxLength,
          verticalLabelsMaxLength,
        } = getDataProperties({ data });
        let ranges = getColorRange({ colors, min, max });
        setState((curState) => ({
          ...curState,
          series,
          options: {
            ...curState.options,
            yaxis: {
              ...curState.options.yaxis,
              labels: {
                ...curState.options.yaxis.labels,
                minWidth: verticalLabelsMaxLength,
                maxWidth: verticalLabelsMaxLength,
              }
            },
            xaxis: {
              ...curState.options.xaxis,
              labels: {
                ...curState.options.xaxis.labels,
                minHeight: horizontalLabelsMaxLength,
                maxHeight: horizontalLabelsMaxLength,
              }
            },
            plotOptions: {
              ...curState.options.plotOptions,
              heatmap: {
                ...curState.options.plotOptions.heatmap,
                colorScale: {
                  ...curState.options.plotOptions.heatmap.colorScale,
                  ranges,
                },
              },
            },
          },
        }));
      }
    }, [loading]);

    if (loading && state.series.length === 0) {
      return <SFSkeleton />;
    }

    return (
      <>
        <Wrappper
          loading={loading}
          className={className}
          autoHeight={autoHeight}
        >
          {loading && (
            <SFStackStyled direction="row" alignItems="center" spacing={1}>
              <SFLoadingIcon />
              <SFTypography variant="body2bold">
                {t("components.heatmapChart.updatingLabel")}
              </SFTypography>
            </SFStackStyled>
          )}
          <CustomLegend
            colorRange={state.options.plotOptions.heatmap.colorScale.ranges}
            chartId={id}
            loading={loading}
            {...legendProps}
          />
          <ReactApexChart
            options={state.options}
            series={state.series}
            type="heatmap"
            //if not autoHeight, we need to set the height and width of the chart
            //this is calculated based on the cell height and width we define in the props
            //it's approximated to the number of cells we have in the data + labels size + some extra padding
            {...(!autoHeight && {
              height:
                (state.series.length ?? 0) * cellHeight +
                30 +
                state.options.xaxis.labels.maxHeight,
              width:
                (state.series[0]?.data?.length ?? 0) * cellWidth +
                30 +
                state.options.yaxis.labels.maxWidth,
            })}
          />
        </Wrappper>
      </>
    );
  }
);

SFHeatmapChart.propTypes = {
  /**
   * Generic structure of data
   * This will transform to the structure needed for the heatmap chart
   * xname is the name of the x axis
   * yname is the name of the y axis
   * count is the value of the cell
   */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      xname: PropTypes.string,
      yname: PropTypes.string,
      count: PropTypes.number,
    })
  ).isRequired,
  /**
   * Array of colors that will be used for the heatmap chart
   * Color is the hex color that will be used
   * Order is the order of the color in the heatmap chart
   */
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      order: PropTypes.number,
    })
  ).isRequired,
  /**
   * Id of the chart
   */
  id: PropTypes.string,
  /**
   * Class name for the chart container
   * Default is null
   */
  className: PropTypes.string,
  /**
   * Loading state of the chart
   * Required in order to display the loading icon and update the chart data
   */
  loading: PropTypes.bool,
  /**
   * Width of the cell in the heatmap chart
   * Default is 50
   */
  cellWidth: PropTypes.number,
  /**
   * Height of the cell in the heatmap chart
   * Default is 50
   */
  cellHeight: PropTypes.number,
  /**
   * Auto height of the chart
   * If true the chart will have auto height and will be responsive to the page
   * In this way the cell height and width will be auto calculated
   * Default is false
   */
  autoHeight: PropTypes.bool,
  /**
   * Export options for the chart
   * Default is fileName: "heatmap"
   */
  exportOptions: PropTypes.shape({
    fileName: PropTypes.string,
  }),
  /**
   * Tooltip formatter for the chart
   * Default is null
   */
  tooltipFormatter: PropTypes.func,
  /**
   * Legend props for the chart
   * Legend formatter is a function that will format the legend labels and tooltips
   * Container is the element where the legend will be rendered
   * Default is legendFormatter: null, container: null
   */
  legendProps: PropTypes.shape({
    legendFormatter: PropTypes.func,
    container: PropTypes.object,
  }),
};

export default SFHeatmapChart;
